import { h, Component, Host, Element, Prop, Watch, State, Listen, Event, EventEmitter } from "@stencil/core";
import {
  StageContentPosition,
  StageSchemeName,
  StageSchemeDefinition,
  StageContentWidth,
  StageContentIndicatorColor,
  StageVideoControlColor,
  StageLocalization
} from "./constants/stage-types";
import {
  STAGE_SCHEME_CONFIG,
  STAGE_HEADLINE_SIZES,
  STAGE_SCHEME_MAP,
  STAGE_INDICATOR_COLOR_LIGHT,
  STAGE_VIDEO_CONTROL_COLOR_LIGHT
} from "./constants/stage-constants";
import {
  applySchemeDelegate,
  setCssVariablesDelegate,
  getSchemeName,
  ColorableComponent
} from "../../common/colorable-component";
import { isMobile } from "../../utils/screen-helper";
import { ASPECTRATIO_2X1, ASPECTRATIO_1X1 } from "../../common/constants";
import { clampHeadlineSizeTo } from "../../utils/headline-helper";
import { getFirstNestedChild, getNestedChildrenOf } from "../../utils/dom-helper";
import pfx from "../../utils/style-class-prefix-helper";
import { HeadlineTag } from "../headline/constants/headline-types";
import { StageSchemeConfiguration } from "../../constants/colors";
import { BackgroundColor } from "../background/constants/background-types";
import {
  createButtonMapDefinition,
  createHeadlineMapDefinition,
  createLinkMapDefinition
} from "../../utils/scheme-helper";
import {
  STAGE_ALIGNMENT,
  STAGE_DEFAULT_ALIGNMENT,
  STAGE_DEFAULT_CONTENT_WIDTH,
  STAGE_DEFAULT_VIOLATOR_HORIZONTAL_POSITION,
  StageAlignment,
  StageViolatorHorizontalPosition
} from "../../constants/global-constants";
import { scrollToElement } from "../../utils/scroll-helper";
import { STAGE_LOCALIZATION } from "./constants/stage-i18n";
import { LocalizableComponent, Localization, setCurrentLocalizationDelegate } from "../../common/localizable-component";
import { stageSvgs } from "./icon-svgs";
import { isTouchDevice } from "../../utils/touch-helper";

/**
 * @slot headline - Holds a single eon-ui-headline element. Use headline sizes between h1 and h3. Other sizes are clamped automatically to ensure brand compliance.
 * @slot image - Holds a single eon-ui-image element
 * @slot ctas - Holds one or two eon-ui-link and/or eon-ui-button elements. Use `button-on-image` if applicable.
 */
@Component({
  tag: "eon-ui-stage",
  styleUrl: "stage.scss",
  assetsDirs: ["assets-stage"],
  shadow: true
})
export class Stage
  implements
    ColorableComponent<StageSchemeConfiguration<StageSchemeDefinition>>,
    LocalizableComponent<StageLocalization> {
  schemeConfig: StageSchemeConfiguration<StageSchemeDefinition> = STAGE_SCHEME_CONFIG;
  schemeMap = STAGE_SCHEME_MAP;

  localization: Localization<StageLocalization> = STAGE_LOCALIZATION;
  currentLocalization: StageLocalization;

  @State() activeScheme: StageSchemeDefinition;
  @State() backgroundVideo;
  @State() allowContentAlignment: boolean = false;
  @State() heightAdjustment;
  @State() isPlaying: boolean = true;

  @Element() hostElement: HTMLEonUiStageElement;

  private headline: HTMLEonUiHeadlineElement;
  private video: HTMLEonUiVideoplayerElement;
  private headlineShadow = false;
  private violatorSize: string;

  /**
   * Raised when the component has been loaded.
   */
  @Event() stageLoad: EventEmitter;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: StageSchemeName;

  /**
   * Represents the internal color of the component.
   * It will be read by colorable-component.ts getColorOfNearestBackground() function to realize
   * the detection of backgrounds inside of components, hidden by shadow dom.
   * @disableControlInStorybook true
   */
  @Prop({ reflect: true, mutable: true }) innerBackground: string;

  /**
   * Enable the colored Contentbox.<br>That means that Headline, Subheadline and other slotted Components have a colored background. The background is dependent from the chosen Scheme<br>
   * E.g.  Scheme "red" enabled the red background, if this property is set to false the scheme "red" ensures that color of the headline and subheadline is red.
   */
  @Prop({ reflect: true }) enableContentOnBackground: boolean = false;

  /**
   * Enable a blurred background for the Contentbox. It <b>only</b> works if the enable-content-on-background-property is true!
   */
  @Prop({ reflect: true }) enableBlurredBackground: boolean = false;

  /**
   * Defines where the content is shown inside the stage
   * @example <eon-ui-stage content-position="center"></eon-ui-stage>
   */
  @Prop() contentPosition: StageContentPosition;

  /**
   * Defines the width of the content is shown inside the stage.<br> - "wide" corresponds to 100%,<br> - "normal" to 75%,<br> - narrow to 50%
   * @default normal
   * @example <eon-ui-stage content-width="normal"></eon-ui-stage>
   */
  @Prop({ mutable: true }) contentWidth: StageContentWidth = STAGE_DEFAULT_CONTENT_WIDTH;

  /**
   * Alignment of the content-box.
   */
  @Prop() alignment: StageAlignment = STAGE_DEFAULT_ALIGNMENT;

  /**
   * Defines the text inside the sub headline
   * @example <eon-ui-stage subheadline-text="this is a subheadline"></eon-ui-stage>
   */
  @Prop({ mutable: true }) subheadlineText: string;

  /**
   * Defines the height of the header of the page where the stage is used. The height of the stage is calculated depending onthis height since
   * both the header and the stage together should take up to 80% of the hight of the viewport. The client should find that value dynamically
   * and pass it down.
   * @example <eon-ui-stage header-height="148"></eon-ui-stage>
   */
  @Prop() headerHeight: number;

  /**
   * this property is used for SEO-Optimisation.<br>
   *   If this property is used and fill-out, this property is used for HTML-Tag for the name- and info-properties,<br>
   *   the HTML-Tag for the info (subheadline) is set automatically depending on this property (headline-html-tag  -1 ), e.g <br>
   *    name(Headline) is H2, then info(subheadline) is H3, name(Headline) is H4, then info(subheadline) is H5, and so on
   *
   */
  @Prop() headlineHtmlTag: HeadlineTag;

  /**
   * Defines the image width and height of the stage to be scaled across the viewport.
   * @example <eon-ui-stage full-height=true></eon-ui-stage>
   */
  @Prop() fullHeight: boolean;

  /**
   * Defines Color of full height indicator
   * @example <eon-ui-stage indicator-color="light"></eon-ui-stage>
   */
  @Prop({ reflect: true }) indicatorColor: StageContentIndicatorColor = STAGE_INDICATOR_COLOR_LIGHT;

  /*
   * Check if violator is available for image
   */
  @State() hasViolator: boolean;

  /**
   * Defines horizontal position of the violator
   */
  // prettier-ignore
  @Prop({ mutable: true }) violatorHorizontalPosition: StageViolatorHorizontalPosition = STAGE_DEFAULT_VIOLATOR_HORIZONTAL_POSITION;

  /**
   * Defines color of pause/resume icon and text
   * @example <eon-ui-stage video-control-color="light"></eon-ui-stage>
   */
  @Prop({ reflect: true }) videoControlColor: StageVideoControlColor = STAGE_VIDEO_CONTROL_COLOR_LIGHT;

  /**
   * Enables the playback option for the video. This property will only be used when video is set to the background.
   */
  @Prop() videoPlaybackOption: boolean = false;

  componentWillLoad() {
    this.headline = getFirstNestedChild(this.hostElement, "eon-ui-headline");
    this.hasViolator = !!getFirstNestedChild(this.hostElement, "eon-ui-violator");
    if (this.hasViolator) {
      this.violatorSize = (getFirstNestedChild(this.hostElement, "eon-ui-violator") as HTMLElement).getAttribute(
        "size"
      );
    }
    this.contentAlignment();
    this.applyScheme();
    this.setCurrentLocalization();
    this.setImageRatio();
    this.video = getFirstNestedChild(this.hostElement, "eon-ui-videoplayer");
    if (this.video) {
      this.setVideoProperties();
    }
    this.video && this.enterViewport(this.hostElement);
    if (this.headline) {
      const size = clampHeadlineSizeTo(STAGE_HEADLINE_SIZES, this.headline.size);
      if (size) this.headline.setAttribute("size", size);
      //run HeadlineAlignment
      this.headlineAlignment();
      if (!this.enableContentOnBackground) {
        this.headlineShadow = this.headline.textShadow;
      } else {
        this.headline.removeAttribute("text-shadow");
      }
      this.handleHeadlineTag();
    }
    this.contentWidth = this.contentWidth ? this.contentWidth : "normal";
    this.handleSubheadlineColor();
    this.updateProcessedText();
    this.passSchemeToChildrenAndHandleSlottedComponents();
    this.fullHeight && this.handleHeightCalculation();
  }

  componentDidLoad() {
    this.setCssVariables();
    this.stageLoad.emit();
  }

  /**
   * This function is calculating the Stage height
   * On basis of whether Navigation or ThirdlevelNavigation is present
   * And then summation value is assigned to "header-height" property for final Stage height calculation.
   */
  @Listen("resize", { target: "window" })
  @Watch("fullHeight")
  async handleHeightCalculation() {
    if (!this.headerHeight) {
      const navigationHeight = await document.getElementsByTagName("eon-ui-navigation")[0]?.updateNavigationHeight();
      const thirdLevelNavigationHeight = await document
        .getElementsByTagName("eon-ui-third-level-navigation")[0]
        ?.updateHeight();
      const bottomDrawerHeight = await document.getElementsByTagName("eon-ui-bottom-drawer")[0]?.getHeight();

      this.heightAdjustment =
        (navigationHeight ? navigationHeight : 0) +
        (thirdLevelNavigationHeight ? thirdLevelNavigationHeight : 0) +
        (bottomDrawerHeight ? bottomDrawerHeight : 0);

      if (Number.isInteger(this.heightAdjustment)) {
        this.hostElement.style.setProperty("--header-height", `${this.heightAdjustment}px`);
      } else {
        this.hostElement.style.setProperty("--header-height", "0px");
      }
    }
  }

  @Watch("headerHeight")
  updateHeaderHeightCSSVariable() {
    if (Number.isInteger(this.headerHeight)) {
      this.hostElement.style.setProperty("--header-height", `${this.headerHeight}px`);
    } else {
      this.hostElement.style.setProperty("--header-height", "0px");
    }
  }

  @Watch("enableContentOnBackground")
  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
    if (this.headline) {
      const defaultScheme = getSchemeName(STAGE_SCHEME_MAP, STAGE_SCHEME_CONFIG, "default");
      if (!this.enableContentOnBackground) {
        this.headline.setAttribute("scheme", this.scheme || defaultScheme);
      }
    }
    this.passSchemeToChildrenAndHandleSlottedComponents();
    this.handleSubheadlineColor();
  }
  handleSubheadlineColor() {
    if (this.enableContentOnBackground) {
      this.hostElement.style.setProperty("--subheadlineColor", `var(--contentBoxSubheadlineColor)`);
    } else {
      this.hostElement.style.setProperty("--subheadlineColor", `var(--${this.activeScheme.subheadlineColor}}`);
    }
  }

  @Watch("activeScheme")
  passSchemeToChildrenAndHandleSlottedComponents() {
    if (!!this.scheme) {
      if (this.enableContentOnBackground) {
        const backgroundColor = this.innerBackground;
        const buttonSchemeMapDefinition = createButtonMapDefinition();
        const headlineSchemeMapDefinition = createHeadlineMapDefinition();
        const linkSchemeMapDefinition = createLinkMapDefinition();
        let buttons = Array.from(getNestedChildrenOf(this.hostElement, "eon-ui-button"));
        buttons.forEach((slotButton) => {
          slotButton.setAttribute("scheme", buttonSchemeMapDefinition[backgroundColor as BackgroundColor]);
          slotButton.removeAttribute("button-on-image");
        });
        let headline = Array.from(getNestedChildrenOf(this.hostElement, "eon-ui-headline"));
        headline.forEach((slotHeadline) =>
          slotHeadline.setAttribute("scheme", headlineSchemeMapDefinition[backgroundColor as BackgroundColor])
        );
        let links = Array.from(getNestedChildrenOf(this.hostElement, "eon-ui-link"));
        links.forEach((slotLink) =>
          slotLink.setAttribute("scheme", linkSchemeMapDefinition[backgroundColor as BackgroundColor])
        );
      }
    }
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
    this.innerBackground = this.activeScheme.contentBoxBackgroundColor;
  }

  /**
   * Executes whenever the text property changes. Replaced the specified linewrap symbol with <br />.
   */
  @Watch("subheadlineText")
  updateProcessedText(): void {
    const slashN = "\\n";
    this.subheadlineText = this.subheadlineText && this.subheadlineText.split(slashN).join("<br />");
  }

  @Listen("resize", { target: "window" })
  private setImageRatio() {
    const aspectRatio = isMobile() ? ASPECTRATIO_1X1 : ASPECTRATIO_2X1;
    const imageComponents = this.hostElement.getElementsByTagName("eon-ui-image");
    if (imageComponents?.length > 0) {
      const image = imageComponents[0];
      image.setAttribute("aspect-ratio", aspectRatio);
      image.setAttribute("use-as-background", "true");
    }
  }

  handleIndicatorClick() {
    scrollToElement(this.hostElement, {
      targetPosition: "end",
      offset:
        this.hostElement.offsetHeight +
        (Number.isInteger(this.heightAdjustment)
          ? this.heightAdjustment
          : window.innerHeight - this.hostElement.offsetHeight)
    });
  }

  @Watch("headlineHtmlTag")
  handleHeadlineTag() {
    if (!!this.headlineHtmlTag) {
      if (this.headline.getAttribute("slot") === "headline") {
        this.headline.setAttribute("headline-html-tag", this.headlineHtmlTag);
      }
    }
  }

  @Watch("alignment")
  contentAlignment() {
    this.allowContentAlignment = (STAGE_ALIGNMENT as ReadonlyArray<string>).includes(this.alignment);
  }

  @Watch("alignment")
  headlineAlignment() {
    if (this.headline && this.allowContentAlignment) {
      this.headline.setAttribute("alignment", this.alignment);
    } else {
      this.headline.setAttribute("alignment", "right");
    }
  }

  setCurrentLocalization() {
    setCurrentLocalizationDelegate(this);
  }

  private setVideoProperties() {
    this.backgroundVideo = Array.from(this.hostElement.children).filter.call(
      this.hostElement.children,
      function (child: HTMLElement) {
        return child.tagName === "EON-UI-VIDEOPLAYER";
      }
    );
    this.backgroundVideo.forEach((backgroundVideo) => {
      backgroundVideo.setAttribute("use-as-background", "true");
    });
  }

  handlePlayToggle() {
    this.isPlaying ? this.video.pausePlayback() : this.video.startPlayback();
    this.isPlaying = !this.isPlaying;
  }

  private enterViewport(element: HTMLElement) {
    if (window && window.IntersectionObserver) {
      const videoObserver = new window.IntersectionObserver(
        (entries: IntersectionObserverEntry[]) => {
          entries.forEach((entry) => {
            if (entry.intersectionRatio > 0) {
              this.isPlaying = true;
            } else {
              this.isPlaying = false;
            }
          });
        },
        {
          threshold: [0, 1]
        }
      );
      videoObserver.observe(element);
    }
  }

  render() {
    const showVideoPlayBackControls =
      (this.video && this.videoPlaybackOption) || (this.video && (isMobile() || isTouchDevice()));

    return (
      <Host>
        <div
          class={pfx({
            stage: true,
            [this.contentPosition]: !!this.contentPosition,
            ["full-height"]: !!this.fullHeight,
            "has-violator": !!this.hasViolator,
            [`violator-${this.violatorSize}`]: !!this.violatorSize
          })}
        >
          {this.video && (
            <div class={pfx("stage-video")}>
              <slot name="video"></slot>
            </div>
          )}
          <div class={pfx("stage-image")}>
            <slot name="image"></slot>
          </div>
          <div
            class={pfx({
              ["content-wrapper"]: true
            })}
          >
            {this.hasViolator && (
              <div
                class={pfx({
                  "stage-violator": true,
                  [this.violatorHorizontalPosition]: true
                })}
              >
                <slot name="violator"></slot>
              </div>
            )}
            <div
              class={pfx({
                ["stage-content"]: true,
                [this.contentWidth]: !!this.contentWidth,
                ["has-colored-background"]: this.enableContentOnBackground,
                ["is-blurred-background"]: this.enableBlurredBackground && this.enableContentOnBackground,
                [this.alignment]: this.allowContentAlignment
              })}
            >
              <div class={pfx("stage-headline")}>
                <slot name="headline"></slot>
              </div>

              {this.subheadlineText && (
                <span
                  class={pfx({
                    ["subheadline"]: true,
                    ["shadow"]: this.headlineShadow,
                    [this.alignment]: this.allowContentAlignment
                  })}
                  innerHTML={this.subheadlineText}
                ></span>
              )}
              <div class={pfx("stage-ctas")}>
                <slot name="cta"></slot>
              </div>
              {showVideoPlayBackControls && (
                <button
                  tabindex="0"
                  class={pfx({
                    ["icon-text-wrapper"]: true,
                    [`video-control-${this.videoControlColor}`]: this.videoControlColor
                  })}
                  onClick={() => this.handlePlayToggle()}
                >
                  <div class={pfx("icon-wrapper")}>{this.isPlaying ? stageSvgs.pause() : stageSvgs.play()}</div>
                  <div class={pfx("text-wrapper")}>
                    <span class={pfx("text")}>
                      {this.isPlaying ? this.currentLocalization.play : this.currentLocalization.pause}
                    </span>
                  </div>
                </button>
              )}
            </div>
          </div>
          {this.fullHeight && (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events
            <div
              class={pfx({
                ["full-height-indicator-wrapper"]: true
              })}
              role="button"
              tabIndex={0}
              onClick={() => this.handleIndicatorClick()}
            >
              <svg
                class={pfx({
                  ["indicator-svg"]: true,
                  [`indicator-${this.indicatorColor}`]: this.indicatorColor
                })}
                xmlns="http://www.w3.org/2000/svg"
                width="48"
                height="48"
                viewBox="0 0 48 48"
              >
                <path
                  class={pfx({
                    ["indicator-path"]: true
                  })}
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M3.698 3.697a7.82 7.82 0 014.07-2.153A86.68 86.68 0 0124.002 0a86.68 86.68 0 0116.232 1.544 7.82 7.82 0 016.224 6.224c2.059 10.821 2.059 21.643 0 32.464a7.82 7.82 0 01-6.224 6.224A86.7 86.7 0 0124 48a86.702 86.702 0 01-16.232-1.544 7.82 7.82 0 01-6.224-6.224c-2.06-10.821-2.06-21.643 0-32.464a7.82 7.82 0 012.153-4.07zm32.109 15.854a.997.997 0 00.164-.55v.002l-.006-.12a1 1 0 00-.765-.856 1 1 0 00-1.066.426c-2.663 4.056-6.098 8.042-9.933 11.527a.358.358 0 01-.46 0c-3.834-3.483-7.269-7.468-9.935-11.527a1.001 1.001 0 00-1.779.364 1.001 1.001 0 00.108.733c2.756 4.198 6.304 8.316 10.26 11.91a2.362 2.362 0 003.152 0c3.957-3.597 7.504-7.715 10.26-11.91z"
                />
              </svg>
            </div>
          )}
        </div>
      </Host>
    );
  }
}
